// What a client company's own people did, newest first. Staff read the full
// audit log instead.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listActivity } from '@/api/activity';
import { Button } from '@/components/Button';

const inputClass = 'w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content';

export function ClientActivity() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const q = useQuery({ queryKey: ['activity', page, search], queryFn: () => listActivity({ page, search }) });
  const rows = q.data?.rows ?? [];
  const pages = q.data ? Math.max(1, Math.ceil(q.data.total / q.data.limit)) : 1;

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-content">Activity</h1>
          <p className="mt-1 text-sm text-content-muted">Changes made by people of your company.</p>
        </div>
        <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          placeholder="Search actions or people" className={`${inputClass} max-w-xs`} />
      </div>

      {q.isLoading ? (
        <p className="text-sm text-content-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <section className="rounded-lg border border-dashed border-border bg-surface/40 p-8 text-center">
          <p className="font-medium text-content">Nothing yet</p>
        </section>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-content-subtle">
                <th className="px-3 py-2 font-normal">When</th>
                <th className="px-3 py-2 font-normal">Who</th>
                <th className="px-3 py-2 font-normal">What</th>
                <th className="px-3 py-2 font-normal">App</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="px-3 py-2 whitespace-nowrap text-content-muted">{new Date(r.ts * 1000).toLocaleString()}</td>
                  <td className="px-3 py-2 text-content-muted">{r.admin_email ?? '—'}</td>
                  <td className="px-3 py-2 text-content">
                    {r.action ?? `${r.method} ${r.path.replace(/^\/api\/v1\/admin/, '')}`}
                    {r.status != null && r.status >= 400 && <span className="ml-2 text-xs text-danger">failed ({r.status})</span>}
                  </td>
                  <td className="px-3 py-2 text-content-muted">{r.app_name ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span className="text-sm text-content-muted">Page {page} of {pages}</span>
          <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
